// =============================================================================
// T-442 — « Offre d'aides » dans la vue à la chaîne (Réponses prêtes) : le texte est validé UNE fois (écran Offre d'aides), puis un lot de 50 part d'un clic.
// Le clic qui lance le lot est la décision ; le serveur exige l'empreinte du texte validé, applique la garde commerciale (opposition, mentions, désinscription)
// et la chaîne d'envoi du Bureau (plafond par heure, copie, journal, mode essai / réel). Un plafond atteint arrête le lot.
// Charte : jetons var(--oh-*) uniquement.
// =============================================================================
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Megaphone } from "lucide-react";

import { OhStatusNote } from "../../../components/ui/oh-charte";
import Bouton from "./boutonBureau";
import { offreAides } from "./bureauAmoApi";

const muted = { color: "var(--oh-text-muted)", fontSize: 14, lineHeight: 1.6 };

/** Résumé d'un lot : « 50 envoyées, 2 refusées » — le mode est dit en toutes lettres. */
export function phraseLot(r) {
  if (!r) return "";
  const mode = r.mode === "reel" ? "Envoi réel" : "Essai : rien n'est parti";
  return `${mode} — ${r.envoyees} fiche${r.envoyees > 1 ? "s" : ""} traitée${r.envoyees > 1 ? "s" : ""}, ${r.refusees?.length || 0} refusée${(r.refusees?.length || 0) > 1 ? "s" : ""}, ${r.restantes} restante${r.restantes > 1 ? "s" : ""}.`;
}

export default function OffreAidesLot({ onChange }) {
  const [etat, setEtat] = useState(null);   // { campagne, compteurs, taille_lot } | null (aucune campagne prête)
  const [occupe, setOccupe] = useState(false);
  const [dernier, setDernier] = useState(null);
  const [confirme, setConfirme] = useState(false);

  const charger = useCallback(async () => {
    try {
      const d = await offreAides.campagnes();
      const valides = (d.campagnes || []).filter((c) => c.statut === "texte_valide");
      const prete = [];
      for (const c of valides) { const k = await offreAides.compteurs(c.id); if (k.pretes > 0) prete.push({ campagne: c, compteurs: k }); }
      setEtat(prete.length ? { ...prete[0], taille_lot: d.taille_lot } : null);
    } catch { setEtat(null); }
  }, []);
  useEffect(() => { charger(); }, [charger]);

  if (!etat) return null;
  const { campagne, compteurs, taille_lot } = etat;
  const n = Math.min(compteurs.pretes, taille_lot);

  const envoyer = async () => {
    setOccupe(true);
    try {
      const r = await offreAides.lot(campagne.id, campagne.sha256);
      setDernier(r); setConfirme(false);
      if (r.arret) toast.error(String(r.arret)); else toast.success(phraseLot(r));
      await charger();
      onChange?.();
    } catch (e) { toast.error(typeof e.detail === "string" ? e.detail : "Envoi impossible, réessayez."); }
    finally { setOccupe(false); }
  };

  return (
    <section data-testid="offre-lot" aria-label="Offre d'aides" style={{ padding: "var(--oh-space-4)", background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)", borderRadius: "var(--oh-radius-card)", marginBottom: "var(--oh-space-4)" }}>
      <div className="flex flex-wrap items-center gap-2">
        <Megaphone className="w-4 h-4" aria-hidden="true" style={{ color: "var(--oh-accent-text)" }} />
        <strong style={{ fontWeight: 500, color: "var(--oh-text)" }} data-testid="offre-lot-titre">Offre d'aides : {compteurs.pretes} fiche{compteurs.pretes > 1 ? "s" : ""} prête{compteurs.pretes > 1 ? "s" : ""}</strong>
        <span style={muted}>{campagne.nom} · texte validé</span>
      </div>
      <p style={{ ...muted, margin: "4px 0 8px" }}>Le texte a été validé une seule fois. Le lot envoie {n} fiche{n > 1 ? "s" : ""} au plus, une par adresse, avec les mentions légales et le lien de désinscription.</p>
      {!confirme ? (
        <Bouton type="primaire" disabled={occupe} data-testid="offre-lot-lancer" onClick={() => setConfirme(true)}>Envoyer un lot de {n}</Bouton>
      ) : (
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Confirmer le lot">
          <span style={{ color: "var(--oh-text)", fontSize: 15 }} data-testid="offre-lot-question">Envoyer {n} offre{n > 1 ? "s" : ""} maintenant ?</span>
          <Bouton type="primaire" disabled={occupe} data-testid="offre-lot-confirmer" onClick={envoyer}>Oui, envoyer</Bouton>
          <Bouton type="discret" disabled={occupe} data-testid="offre-lot-annuler" onClick={() => setConfirme(false)}>Non</Bouton>
        </div>
      )}
      {dernier && (
        <div style={{ marginTop: 8 }}>
          <OhStatusNote variant={dernier.arret ? "error" : "info"} data-testid="offre-lot-resultat" title={dernier.arret ? "Lot arrêté" : "Lot traité"}>{phraseLot(dernier)}{dernier.arret ? ` ${dernier.arret}` : ""}</OhStatusNote>
        </div>
      )}
    </section>
  );
}
